import React, { useEffect, useState, useRef } from 'react';
import { TransactionApiData, XmlStatus } from '../../../shared/types';
import { TransactionApi } from '../../../infrastructure/api';
import { formatCurrency, getXmlRecusaMensagem, getXmlStatus } from '../../../shared/utils';
import { useReceiptPrint } from '../../hooks';
import { XmlUploadLocal } from '../../hooks/useXmlUpload';

interface LastSalesProps {
    supplierId: number | null;
    token: string | null;
    showLastSales: boolean;
    onToggle: () => void;
    isAlwaysOpen?: boolean;
    onAddXml?: (transactionId: number, file: File) => void;
    xmlUploads?: Map<number, XmlUploadLocal>;
}

export const LastSales: React.FC<LastSalesProps> = ({
    supplierId,
    token,
    showLastSales,
    onToggle,
    isAlwaysOpen = false,
    onAddXml,
    xmlUploads = new Map()
}) => {
    const [transactions, setTransactions] = useState<TransactionApiData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { printReceipt, printingId } = useReceiptPrint(supplierId, token);
    const isInitialLoadRef = useRef<boolean>(true);

    useEffect(() => {
        const loadTransactions = async () => {
            if (!supplierId || !token) {
                if (isInitialLoadRef.current) {
                    setTransactions([]);
                }
                return;
            }

            try {
                setIsLoading(true);
                const apiData = await TransactionApi.fetchBySupplierId(supplierId, token);

                setTransactions(prevTransactions => {
                    if (isInitialLoadRef.current) {
                        isInitialLoadRef.current = false;
                        return apiData;
                    }

                    // atualiza as vendas já exibidas (ex.: status do XML) e adiciona as novas no topo
                    const apiById = new Map(apiData.map(t => [t.mig_tra_id, t]));
                    const existingIds = new Set(prevTransactions.map(t => t.mig_tra_id));
                    const newTransactions = apiData.filter(t => !existingIds.has(t.mig_tra_id));
                    const updatedTransactions = prevTransactions.map(t => apiById.get(t.mig_tra_id) ?? t);

                    return [...newTransactions, ...updatedTransactions];
                });
            } catch (error) {
                console.error('Error loading transactions:', error);
                if (isInitialLoadRef.current) {
                    setTransactions([]);
                }
            } finally {
                setIsLoading(false);
            }
        };

        if (showLastSales) {
            loadTransactions();

            const interval = setInterval(() => {
                loadTransactions();
            }, 60000);

            return () => {
                clearInterval(interval);
            };
        } else {
            isInitialLoadRef.current = true;
        }
    }, [supplierId, token, showLastSales]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatValue = (value: string) => {
        return formatCurrency(parseFloat(value));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, transactionId: number) => {
        e.stopPropagation();
        const file = e.target.files?.[0];
        if (file && onAddXml) {
            onAddXml(transactionId, file);
        }
        if (e.target) {
            e.target.value = '';
        }
    };

    // o envio feito nesta sessão vale até o back retornar esse XML (ou um mais novo)
    const resolveXmlStatus = (transaction: TransactionApiData): { status: XmlStatus; motivo: string | null } => {
        const local = xmlUploads.get(transaction.mig_tra_id);
        const backendStatus = transaction.xml_status ?? (transaction.xml_anexado === '1' ? 'vinculado' : 'sem_xml');

        if (local && backendStatus !== 'vinculado' && (transaction.xml_id ?? 0) < local.xmlId) {
            return { status: getXmlStatus(local.motivo), motivo: local.motivo };
        }

        return { status: backendStatus, motivo: transaction.xml_motivo ?? null };
    };

    const renderXmlUploadButton = (transactionId: number, title: string, icon: string, className = '') => (
        <label className={`xml-icon-button ${className}`} title={title}>
            {icon}
            <input
                type="file"
                accept=".xml"
                onChange={(e) => handleFileChange(e, transactionId)}
                style={{ display: 'none' }}
            />
        </label>
    );

    const renderXmlAction = (transaction: TransactionApiData) => {
        const { status, motivo } = resolveXmlStatus(transaction);

        switch (status) {
            case 'vinculado':
                return (
                    <span className="xml-success-icon" title="XML vinculado">
                        ✓
                    </span>
                );
            case 'processando':
                return (
                    <span className="xml-pending-icon" title="XML enviado, em processamento">
                        ⏳
                    </span>
                );
            case 'recusado':
                return renderXmlUploadButton(
                    transaction.mig_tra_id,
                    `${getXmlRecusaMensagem(motivo)} Clique para reenviar.`,
                    '⚠️',
                    'xml-rejected'
                );
            default:
                return renderXmlUploadButton(transaction.mig_tra_id, 'Anexar XML', '📄');
        }
    };

    const handleDownloadReceipt = (transaction: TransactionApiData) => {
        printReceipt({ transactionId: transaction.mig_tra_id });
    };

    return (
        <div className="last-sales">
            <div
                className={`sales-header-toggle ${isAlwaysOpen ? 'always-open' : ''}`}
                onClick={isAlwaysOpen ? undefined : onToggle}
            >
                <h3>
                    Últimas Vendas
                    {isLoading && !isInitialLoadRef.current && transactions.length > 0 && (
                        <span className="loading-indicator-header">
                            <span className="loading-spinner-small"></span>
                        </span>
                    )}
                </h3>
                <span className={`toggle-icon ${showLastSales ? 'expanded' : ''}`}>
                    ▼
                </span>
            </div>
            {showLastSales && (
                <div className="sales-table">
                    <div className="sales-header">
                        <div className="sales-cell">Autorização</div>
                        <div className="sales-cell">Data</div>
                        <div className="sales-cell">Produto</div>
                        <div className="sales-cell">Valor</div>
                        <div className="sales-cell">Quantidade</div>
                        <div className="sales-cell">Placa</div>
                        <div className="sales-cell">Ações</div>
                    </div>
                    {isLoading && isInitialLoadRef.current && transactions.length === 0 ? (
                        <div className="sales-row">
                            <div className="sales-cell" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>
                                Carregando...
                            </div>
                        </div>
                    ) : transactions.length === 0 ? (
                        <div className="sales-row">
                            <div className="sales-cell" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>
                                Nenhuma transação encontrada
                            </div>
                        </div>
                    ) : (
                        transactions.map((transaction) => (
                            <div key={transaction.mig_tra_id} className="sales-row">
                                <div className="sales-cell" data-label="Autorização:">{transaction.mig_tra_autorizacao}</div>
                                <div className="sales-cell" data-label="Data:">{transaction.mig_tra_data}</div>
                                <div className="sales-cell" data-label="Produto:">{transaction.mig_pro_produtos}</div>
                                <div className="sales-cell" data-label="Valor:">{formatValue(transaction.mig_tra_valor)}</div>
                                <div className="sales-cell" data-label="Quantidade:">{transaction.mig_tra_quantidade}</div>
                                <div className="sales-cell" data-label="Placa:">{transaction.mig_vei_placa}</div>
                                <div className="sales-cell" data-label="Ações:">
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <button
                                            className="receipt-download-button"
                                            onClick={() => handleDownloadReceipt(transaction)}
                                            disabled={printingId === transaction.mig_tra_id}
                                            title="Baixar Comprovante"
                                        >
                                            {printingId === transaction.mig_tra_id ? '⏳' : '📥'}
                                        </button>
                                        {renderXmlAction(transaction)}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};
